/**
 * System MCP Templates Management Page.
 *
 * Admin-only page for managing template lifecycle and CRUD operations.
 * Provides create, edit, delete, and lifecycle transitions.
 *
 * Sprint: 41, Task: S41-A04/A06 (enhanced from S40-B06)
 */

import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/utils';
import {
  Button,
  Card,
  SearchInput,
  Spinner,
  StatusBadge,
  TemplateIcon,
} from '@/components/ui';
import {
  useSystemTemplates,
  useUpdateSystemTemplate,
  useDeleteSystemTemplate,
} from '@/features/mcp';
import type {
  MCPTemplateSummary,
  TemplateLifecycleStatus,
} from '@/features/mcp';
import {
  Settings2,
  ArrowRight,
  ArchiveRestore,
  Plus,
  Pencil,
  Trash2,
  MoreVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import { useState, useRef, useEffect } from 'react';

// =============================================================================
// Constants
// =============================================================================

const VALID_TRANSITIONS: Record<string, TemplateLifecycleStatus[]> = {
  created: ['preview'],
  preview: ['published'],
  published: ['retired'],
  retired: ['preview'],
};

// =============================================================================
// Helpers
// =============================================================================

function lifecycleStatusType(status: TemplateLifecycleStatus) {
  switch (status) {
    case 'published': return 'healthy' as const;
    case 'preview': return 'warning' as const;
    case 'created': return 'default' as const;
    case 'retired': return 'error' as const;
    default: return 'default' as const;
  }
}

function getPromoteAction(
  status: TemplateLifecycleStatus,
  t: (key: string) => string
): { label: string; target: TemplateLifecycleStatus; confirm: string; successKey: string } | null {
  const targets = VALID_TRANSITIONS[status] ?? [];
  if (targets.length === 0) return null;
  const target = targets[0];

  switch (status) {
    case 'created':
      return { label: t('admin.systemTemplates.promote'), target, confirm: t('admin.systemTemplates.confirmPromote'), successKey: 'promoteSuccess' };
    case 'preview':
      return { label: t('admin.systemTemplates.promote'), target, confirm: t('admin.systemTemplates.confirmPromote'), successKey: 'promoteSuccess' };
    case 'published':
      return { label: t('admin.systemTemplates.retire'), target, confirm: t('admin.systemTemplates.confirmRetire'), successKey: 'retireSuccess' };
    case 'retired':
      return { label: t('admin.systemTemplates.reactivate'), target, confirm: t('admin.systemTemplates.confirmReactivate'), successKey: 'reactivateSuccess' };
    default:
      return null;
  }
}

// =============================================================================
// Dropdown Menu Component
// =============================================================================

function RowActionsMenu({
  template,
  onTransition,
  onEdit,
  onDelete,
  isPending,
}: {
  template: MCPTemplateSummary;
  onTransition: (slug: string, target: TemplateLifecycleStatus, successKey: string) => void;
  onEdit: (slug: string) => void;
  onDelete: (slug: string, displayName: string) => void;
  isPending: boolean;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const action = getPromoteAction(template.lifecycle_status as TemplateLifecycleStatus, t);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < 200);
    }
    setOpen(!open);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-1 rounded hover:bg-muted transition-colors"
        disabled={isPending}
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className={cn(
          'absolute right-0 w-48 bg-popover border rounded-md shadow-md z-50 py-1',
          dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
        )}>
          <button
            className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2"
            onClick={() => { setOpen(false); onEdit(template.slug); }}
          >
            <Pencil className="w-3.5 h-3.5" />
            {t('common.edit')}
          </button>
          {action && (
            <button
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2"
              onClick={() => {
                setOpen(false);
                if (window.confirm(action.confirm)) {
                  onTransition(template.slug, action.target, action.successKey);
                }
              }}
            >
              <ArrowRight className="w-3.5 h-3.5" />
              {action.label}
            </button>
          )}
          <div className="border-t my-1" />
          <button
            className="w-full text-left px-3 py-1.5 text-sm hover:bg-destructive/10 text-destructive flex items-center gap-2"
            onClick={() => { setOpen(false); onDelete(template.slug, template.display_name); }}
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t('admin.systemTemplates.deleteTemplate')}
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Template Row
// =============================================================================

function TemplateRow({
  template,
  onTransition,
  onEdit,
  onDelete,
  isPending,
}: {
  template: MCPTemplateSummary;
  onTransition: (slug: string, target: TemplateLifecycleStatus, successKey: string) => void;
  onEdit: (slug: string) => void;
  onDelete: (slug: string, displayName: string) => void;
  isPending: boolean;
}) {
  const { t } = useI18n();

  return (
    <tr className="border-b hover:bg-muted/30 transition-colors">
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <TemplateIcon icon={template.icon} size="sm" className="text-primary" hasCustomIcon={template.has_custom_icon} slug={template.slug} />
          </div>
          <div>
            <button
              onClick={() => onEdit(template.slug)}
              className="font-medium text-sm hover:text-primary transition-colors text-left"
            >
              {template.display_name}
            </button>
            <p className="text-xs text-muted-foreground">{template.slug}</p>
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <span className="text-xs capitalize">{template.category}</span>
      </td>
      <td className="py-3 px-4">
        <StatusBadge
          status={lifecycleStatusType(template.lifecycle_status as TemplateLifecycleStatus)}
          label={t(`admin.systemTemplates.status.${template.lifecycle_status}`)}
        />
      </td>
      <td className="py-3 px-4 text-center">
        <span className="text-sm">{template.tools_count}</span>
      </td>
      <td className="py-3 px-4 text-center">
        {template.is_beta && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
            Beta
          </span>
        )}
      </td>
      <td className="py-3 px-4 text-right">
        <RowActionsMenu
          template={template}
          onTransition={onTransition}
          onEdit={onEdit}
          onDelete={onDelete}
          isPending={isPending}
        />
      </td>
    </tr>
  );
}

// =============================================================================
// Page Component
// =============================================================================

export function SystemTemplatesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useSystemTemplates();
  const updateMutation = useUpdateSystemTemplate();
  const deleteMutation = useDeleteSystemTemplate();

  const templates: MCPTemplateSummary[] =
    (data as { templates?: MCPTemplateSummary[] })?.templates ?? (Array.isArray(data) ? data : []);

  const handleTransition = async (slug: string, target: TemplateLifecycleStatus, successKey: string) => {
    try {
      await updateMutation.mutateAsync({ slug, data: { lifecycle_status: target } });
      toast.success(t(`admin.systemTemplates.${successKey}`));
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update template');
    }
  };

  const handleEdit = (slug: string) => {
    navigate(`/integrations/templates/${slug}/edit`);
  };

  const handleDelete = async (slug: string, displayName: string) => {
    const msg = t('admin.systemTemplates.confirmDelete').replace('{name}', displayName);
    if (!window.confirm(msg)) return;
    try {
      await deleteMutation.mutateAsync(slug);
      toast.success(t('admin.systemTemplates.deleteSuccess'));
    } catch (err) {
      toast.error((err as Error).message || 'Failed to delete template');
    }
  };

  // Group by lifecycle status for a clear overview
  const statusOrder: TemplateLifecycleStatus[] = ['published', 'preview', 'created', 'retired'];
  const sorted = [...templates].sort((a, b) => {
    const ai = statusOrder.indexOf(a.lifecycle_status as TemplateLifecycleStatus);
    const bi = statusOrder.indexOf(b.lifecycle_status as TemplateLifecycleStatus);
    if (ai !== bi) return ai - bi;
    return a.display_name.localeCompare(b.display_name);
  });

  // Client-side search filter
  const filtered = search
    ? sorted.filter(
        (tpl) =>
          tpl.display_name.toLowerCase().includes(search.toLowerCase()) ||
          tpl.slug.toLowerCase().includes(search.toLowerCase()) ||
          tpl.category.toLowerCase().includes(search.toLowerCase()) ||
          tpl.description?.toLowerCase().includes(search.toLowerCase())
      )
    : sorted;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="w-6 h-6 text-primary" />
            {t('admin.systemTemplates.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('admin.systemTemplates.subtitle')}
          </p>
        </div>
        <Button onClick={() => navigate('/integrations/templates/new')}>
          <Plus className="w-4 h-4 mr-2" />
          {t('admin.systemTemplates.createTemplate')}
        </Button>
      </div>

      {/* Search + Lifecycle legend */}
      <div className="flex items-center gap-4">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('admin.systemTemplates.search')}
          className="max-w-sm"
        />
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-6 text-xs text-muted-foreground">
          <span className="font-medium">{t('admin.systemTemplates.lifecycle')}:</span>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-muted-foreground" />
            Created
            <ArrowRight className="w-3 h-3" />
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            Preview
            <ArrowRight className="w-3 h-3" />
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            Published
            <ArrowRight className="w-3 h-3" />
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-destructive" />
            Retired
          </div>
          <div className="flex items-center gap-1.5 ml-4 text-muted-foreground/70">
            <ArchiveRestore className="w-3 h-3" />
            Retired → Preview (reactivate)
          </div>
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      ) : error ? (
        <Card className="p-8 text-center text-destructive">
          Failed to load templates
        </Card>
      ) : sorted.length === 0 ? (
        <Card className="p-12 text-center">
          <Settings2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">
            {t('admin.systemTemplates.noTemplates')}
          </p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <Settings2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">
            {t('admin.systemTemplates.noResults')}
          </p>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground border-b">
                <th className="text-left py-2.5 px-4 font-medium">Template</th>
                <th className="text-left py-2.5 px-4 font-medium">Category</th>
                <th className="text-left py-2.5 px-4 font-medium">Status</th>
                <th className="text-center py-2.5 px-4 font-medium">Tools</th>
                <th className="text-center py-2.5 px-4 font-medium">Flags</th>
                <th className="text-right py-2.5 px-4 font-medium">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((tpl) => (
                <TemplateRow
                  key={tpl.id}
                  template={tpl}
                  onTransition={handleTransition}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isPending={updateMutation.isPending || deleteMutation.isPending}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
